import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize, retry } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { DatacloudTable } from '../../models/appcloud/datacloud-table.model';

@Injectable({
    providedIn: 'root',
})
export class DatacloudTableService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private DATACLOUD_TABLES_URL: string = environment.publicApiHost + '/appcloud/datacloud-tables';

    getById(id: number, additionalHeaders: any = {}): Observable<DatacloudTable> {
        this.#spinnerService.addProcess('DatacloudTablesService.getById.' + id, 'Loading your tables...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<DatacloudTable>(`${this.DATACLOUD_TABLES_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving table data from Relationship One. Please try again later.'
                );
                return of<DatacloudTable>();
            }),
            finalize(() => this.#spinnerService.completeProcess('DatacloudTablesService.getById.' + id))
        );
    }

    findAll(eloquainstallId: number, additionalHeaders: any = {}): Observable<DatacloudTable[]> {
        this.#spinnerService.addProcess('DatacloudTablesService.findAll.' + eloquainstallId, 'Loading your tables...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', eloquainstallId.toString()),
        };

        return this.#http.get<DatacloudTable[]>(this.DATACLOUD_TABLES_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving table data from Relationship One. Please try again later.'
                );
                return of<DatacloudTable[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('DatacloudTablesService.findAll.' + eloquainstallId))
        );
    }

    create(dbTable: DatacloudTable, additionalHeaders: any = {}): Observable<DatacloudTable> {
        this.#spinnerService.addProcess('DatacloudTablesService.create', 'Creating your datacloud table...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<DatacloudTable>(this.DATACLOUD_TABLES_URL, dbTable, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while saving your table. Please try again later.'
                );
                return of<DatacloudTable>();
            }),
            finalize(() => {
                this.#spinnerService.completeProcess('DatacloudTablesService.create');
            })
        );
    }

    update(id: number, datacloudTable: DatacloudTable, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess('DatacloudTablesService.update.' + id, 'Updaing your table configuration...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.patch<DatacloudTable>(this.DATACLOUD_TABLES_URL + '/' + id, datacloudTable, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while updating the table. Please try again later.'
                );
                return of<DatacloudTable>();
            }),
            finalize(() => this.#spinnerService.completeProcess('DatacloudTablesService.update.' + id))
        );
    }

    delete(id: number, additionalHeaders: any = {}): Observable<DatacloudTable[]> {
        const spinnerProcess = 'DatacloudTableService.delete';
        this.#spinnerService.addProcess(spinnerProcess, 'Deleting your table...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const tableDropURL = this.DATACLOUD_TABLES_URL + `/${id}`;

        return this.#http.delete<DatacloudTable[]>(tableDropURL, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to delete the table. Please try again later.'
                );
                return of<DatacloudTable[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcess))
        );
    }
}
